import { useResponseRedirect, useSectionProgress } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import { useAuth } from "src/context";
import {
  fromBorrowerPersonalInfo,
  toEmploymentStatusUpdateReq,
  type PersonalInfoFormData,
} from "src/api/adapters/borrower";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import {
  FormTable,
  FormTableBody,
  RadioGroup,
  RadioGroupCardItem,
  WorkHistoryIcon,
} from "@porchpass/ui";

type EmploymentStatusValue = "UNEMPLOYED" | "RETIRED" | "OTHER";

export default () => {
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const sectionProgress = useSectionProgress();
  const { formData } = useFormLoad<PersonalInfoFormData>(
    {},
    () => api.getBorrower(),
    (data) =>
      fromBorrowerPersonalInfo(
        data as Parameters<typeof fromBorrowerPersonalInfo>[0],
      ),
    { enabled: !!firebaseUser },
  );
  const [submitHandler, loading, response] = useDlaFormSubmit(
    async (data: PersonalInfoFormData) => {
      return api.updateBorrower(toEmploymentStatusUpdateReq(data));
    },
  );
  let nextPagePath = "/income";
  if (sectionProgress["employment"] && sectionProgress["income"])
    nextPagePath = "../../progress";
  useResponseRedirect(nextPagePath, response);

  const handleStatusChange = (status: EmploymentStatusValue) => {
    void submitHandler({ employment_status: status });
  };

  return (
    <FormLayout
      title="What's your current status?"
      headerTitle="Employment Information"
      stepIndex={3}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Employment Status"
          headerIcon={
            <WorkHistoryIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
        >
          <FormTableBody>
            <RadioGroup
              value={formData?.employment_status ?? ""}
              onValueChange={(value) =>
                handleStatusChange(value as EmploymentStatusValue)
              }
              onItemReselect={(value) =>
                handleStatusChange(value as EmploymentStatusValue)
              }
              className="flex flex-wrap gap-2"
            >
              <RadioGroupCardItem
                value="UNEMPLOYED"
                label="Unemployed"
                variant="inline"
              />
              <RadioGroupCardItem
                value="RETIRED"
                label="Retired"
                variant="inline"
              />
              <RadioGroupCardItem
                value="OTHER"
                label="Other"
                variant="inline"
              />
            </RadioGroup>
          </FormTableBody>
        </FormTable>
      </Col>
    </FormLayout>
  );
};
